import { extend } from 'umi-request'
import { apiUrl, apiUrlDrama } from './url'
import {
  sign,
  getTextByKey,
  tracking,
  subscriptionNetworkStatus
} from '@feedback/utils/util'
import {
  applyVestSiteToSession,
  getVestApiUrl,
  initVestSiteIdentity,
  isVestAppid,
  resolveDebugAppid
} from '@feedback/utils/siteConfig'
import { Toast } from 'antd-mobile'
import ObjectPick from 'lodash/pick'
import qs from 'qs'

/**
 * 仅 dev / test 调试：马甲站页面带 ?debug-appid=<appid> 时，把「出站请求的
 * appid」换成该值（借其它 appid 的 FAQ/表单/工单数据）。sessionStorage 的
 * appid / vest 皮肤 / 身份保持不变，仅改发给后端的 appid。
 * prod / gray 及非 vest 身份下 resolveDebugAppid 恒返回 null，此处恒等返回。
 */
const applyVestDebugAppid = (params) => {
  if (!params?.appid || !isVestAppid(params.appid)) return params
  const debugAppid = resolveDebugAppid()
  if (!debugAppid) return params
  return { ...params, appid: debugAppid }
}

/**
 * @des  路由全放在参数的action字段中
 *
 * 马甲站：在写入 appid / 换签前先完成 hostname（或 URL site 参数）→ 主体 appid
 * 解析，保证 getSign 与 getAppConfig 读到正确身份。非马甲站域名/参数零侵入。
 * 服务端已为主体 appid（cm1017-sweet / cm1017-b25）配置数据，dev 直接用真实
 * appid；调试借数据改由 ?debug-appid 显式覆盖出站 appid（见 applyVestDebugAppid）。
 */
export const handleUrlParams = () => {
  return applyVestDebugAppid(handleUrlParamsInner())
}

const handleUrlParamsInner = () => {
  const hashParamsRaw = qs.parse(window.location?.hash?.split?.('?')?.[1])
  const hashParams = Object.fromEntries(
    Object.entries(hashParamsRaw).map(([k, v]) => [decodeURIComponent(k), v || ''])
  )

  // 马甲站身份解析（hostname / site 参数）；未命中时不改 sessionStorage
  const vestSite = initVestSiteIdentity(hashParams)

  let param = ObjectPick(hashParams, [
    'appid',
    'uuid',
    'did',
    'deviceName',
    'devicePlatform',
    'version',
    'package-name',
    'channel',
    'language',
    'from',
    'caseType',
    'network',
    'contextTitle'
  ])

  // 命中马甲站时，主体 appid 以站点配置为准（覆盖 URL 里误带的 appid）
  if (vestSite) {
    param = { ...param, appid: vestSite.appid }
  }

  if (param?.appid && param?.did) {
    sessionStorage.setItem('headerFromApp', JSON.stringify(param))
    sessionStorage.setItem('appid', (param?.appid).toString())
    // header 已写入后再同步一次，保证 site* 键与 header.appid 一致
    if (vestSite) {
      applyVestSiteToSession(vestSite)
    }
    return {
      ...param,
      // ...(param?.appid == 'cm1015' ? { appid: 'cm1001' } : {}) // TODO: 需要删除
    }
  }

  const params2 = ObjectPick(hashParams, [
    '_app_id',
    '_app_lang',
    '_app_channel_id',
    '_app_version',
    '_app_game_version',
    '_app_res_version',
    '_app_install_id',
    '_app_activate_id',
    '_app_user_id',
    '_device_id',
    '_device_brand',
    '_device_model',
    '_os_type',
    '_os_version',
    '_ad_id',
    '_androidid',
    '_idfv',
    'package-name'
  ])

  if (params2?.['_app_id'] && !vestSite) {
    const headerData = {
      appid: params2?.['_app_id'],
      caseType: 2,
      channel: params2?.['_app_channel_id'],
      deviceName: params2?.['_device_model'],
      devicePlatform: `${params2?.['_os_type']} ${params2?.['_os_version']}`,
      did: params2?.['_device_id'],
      from: 'HelpFeedback',
      language: params2?.['_app_lang'],
      'package-name': params2?.['package-name'],
      uuid: params2?.['_app_user_id'],
      version: params2?.['_app_version']
    }
    sessionStorage.setItem('headerFromApp', JSON.stringify(headerData))
    sessionStorage.setItem('tracking-data', JSON.stringify(params2))
    sessionStorage.setItem('appid', (params2?.['_app_id']).toString())
    return headerData
  }

  // 马甲站 + 下划线风格参数：以 vest appid 为主体，其余字段仍映射
  if (params2?.['_app_id'] && vestSite) {
    const headerData = {
      appid: vestSite.appid,
      caseType: 2,
      channel: params2?.['_app_channel_id'],
      deviceName: params2?.['_device_model'],
      devicePlatform: `${params2?.['_os_type']} ${params2?.['_os_version']}`,
      did: params2?.['_device_id'],
      from: 'HelpFeedback',
      language: params2?.['_app_lang'],
      'package-name': params2?.['package-name'],
      uuid: params2?.['_app_user_id'],
      version: params2?.['_app_version']
    }
    sessionStorage.setItem('headerFromApp', JSON.stringify(headerData))
    sessionStorage.setItem('tracking-data', JSON.stringify(params2))
    sessionStorage.setItem('appid', vestSite.appid)
    applyVestSiteToSession(vestSite)
    return headerData
  }

  const headerFromApp = sessionStorage.getItem('headerFromApp')
  const params = headerFromApp ? JSON.parse(headerFromApp) : {}

  // 马甲站首次进入且尚无 header：至少带上主体 appid，供后续 sign / 配置读取
  if (vestSite && !params?.appid) {
    params.appid = vestSite.appid
    sessionStorage.setItem('appid', vestSite.appid)
  }

  if (!params?.hasOwnProperty('network')) {
    subscriptionNetworkStatus((status) => {
      params.network = Object.keys(networkCode)?.includes(status)
        ? networkCode[status]
        : networkCode?.['unknown']
    })
  }

  return { ...params }
}

const networkCode = {
  wifi: 1,
  offline: 0,
  unknown: 200,
  '2g': 202,
  '3g': 203,
  '4g': 204,
  '5g': 205
}

/** 701 参数错误自动 reload 的连续计数键（见 errorHandler） */
const RELOAD_701_COUNT_KEY = 'reload701Count'

const getCodeMessage = () => {
  const appid = sessionStorage.getItem('appid')
  const headerFromApp = JSON.parse(sessionStorage.getItem('headerFromApp'))
  let errMsg401 = getTextByKey(
    'System error. Please reopen the Feedback page by going to {AppName} App → Help Center.'
  )
  switch (appid) {
    case 'cm1001':
      errMsg401 = errMsg401.replace('{AppName}', 'Chapters')
      break
    case 'cm1003':
      errMsg401 = errMsg401.replace('{AppName}', 'MyFiction')
      break
    case 'cm1006':
      errMsg401 = errMsg401.replace('{AppName}', 'パラナイツ')
      break
    case 'cm1007':
      errMsg401 = errMsg401.replace('{AppName}', 'Magic')
      break
    case 'cm1008':
      errMsg401 = errMsg401.replace('{AppName}', 'Escape')
      break
    case 'cm1017':
    // falls through (cm1017 与 cm1009 共享 Reelshort 品牌文案)
    case 'cm1009':
      errMsg401 = errMsg401.replace('{AppName}', 'Reelshort')
      if (headerFromApp?.language?.includes('fr')) {
        errMsg401 = getTextByKey(
          'cm1009.fr.System error. Please reopen the Feedback page by going to {AppName} App → Help Center.'
        ).replace('{AppName}', 'Reelshort')
      }
      break
    case 'cm1011':
      errMsg401 = errMsg401.replace('{AppName}', 'MatchMe')
      break
    case 'cm1012':
      errMsg401 = errMsg401.replace('{AppName}', 'MaxDrama')
      break
    case 'cm1015':
      errMsg401 = errMsg401.replace('{AppName}', 'MergeWorld')
    case 'cm3002':
      errMsg401 = errMsg401.replace('{AppName}', 'Myfiction')
      break
    case 'cm1013':
      errMsg401 = errMsg401.replace('{AppName}', 'RSNovel')
      break
    case 'cm1018':
      errMsg401 = errMsg401.replace('{AppName}', 'Gorogoro')
      break
    default:
      errMsg401 = errMsg401.replace('{AppName}', 'Chapters')
      break
  }
  return {
    401: errMsg401,
    601:
      ['cm1009', 'cm1017'].includes(appid) && headerFromApp?.language?.includes('fr')
        ? getTextByKey('cm1009.fr.System error. Sign expired.')
        : getTextByKey('System error. Sign expired.')
  }
}

const errorHandler = (error) => {
  // J项目埋点错误失败 6s 重试一次
  if (error?.request?.options?.data?.action === 'userActionLog.addLog') {
    setTimeout(() => {
      tracking(error?.request?.options?.data)
    }, 6 * 1000)
  }

  const codeMessage = getCodeMessage()

  if (error?.name && error?.name === 'RequestError') {
    // 请求超时
    Toast?.offline(getTextByKey('network error'), 3)
  }

  if (error === 'TypeError: Failed to fetch') {
    Toast.offline(getTextByKey('network error'), 3)
  }

  // 签名校验失败
  if (error?.data?.code === 401 && sessionStorage.getItem('sign')) {
    sessionStorage.removeItem('sign')
    sessionStorage.removeItem('headerFromApp')
    sessionStorage.setItem('errMsg', codeMessage?.[error?.data?.code])
    Toast.info(codeMessage?.[error?.data?.code])
    location.replace(location?.href?.split?.('#')?.[0] + '#/error')
  }

  // 签名过期 && 本地存在 App 数据
  if (error?.data?.code === 601 && sessionStorage.getItem('headerFromApp')) {
    sessionStorage.removeItem('sign')
    location.replace()
  }

  if (error?.data?.code === 701 && Object.keys(handleUrlParams()).length) {
    // 参数错误：reload 最多连续 2 次（任一请求成功后计数清零）。
    // 服务端持续 701（如 header 缺参）时无护栏会进入无限整页重载。
    const reloadCount = Number(
      sessionStorage.getItem(RELOAD_701_COUNT_KEY) || 0
    )
    if (reloadCount < 2) {
      sessionStorage.setItem(RELOAD_701_COUNT_KEY, String(reloadCount + 1))
      location.reload()
    }
  }

  return {
    ...error.data,
    message:
      codeMessage?.[error?.data?.code] ||
      error?.data?.msg ||
      error?.data?.message
  }
}

const headerUrlParams = handleUrlParams()
// 马甲站独立域名：接口走同域名系的代理 feedback-api-<slug>.<base>；
// 共享域名 / localhost 下为 null，沿用默认 apiUrl（见 getVestApiUrl）
const vestApiUrl = getVestApiUrl()
const request = extend({
  errorHandler,
  prefix: apiUrl,
  headers: {
    ...headerUrlParams
  },
  // MaxDrama 项目特殊处理
  ...(headerUrlParams?.appid === 'cm1012'
    ? {
        prefix: apiUrlDrama
      }
    : {}),
  ...(vestApiUrl ? { prefix: vestApiUrl } : {}),
  timeout: 60 * 1000
})

// 请求成功即重置 701 reload 计数，保证护栏只拦「连续」失败
request.interceptors.response.use((response) => {
  if (response?.ok) {
    sessionStorage.removeItem(RELOAD_701_COUNT_KEY)
  }
  return response
})

export default request
